<template>
  <div v-if="paginatedHub">
    <div
      class="flex flex-col md:flex-row-reverse md:justify-between md:items-center gap-4 md:gap-0"
    >
      <DashboardSearch />

      <h1 class="font-bold text-4xl text-on-background md:whitespace-nowrap">
        <template v-if="props.title">{{ props.title }}</template>
        <template
          v-else-if="
            organization?.isDashboardTitleEnabled &&
            Boolean(organization.dashboardTitleText?.length)
          "
        >
          {{ organization.dashboardTitleText }}
        </template>
        <template v-else>
          {{ organization?.name }} {{ $t('common.dashboard') }}
        </template>
      </h1>
    </div>

    <div class="font-semibold text-on-background-variant">
      {{ paginatedHub.totalCount }} {{ $t('common.hubs') }}
    </div>

    <Breadcrumbs v-if="breadcrumbsItems !== null" :items="breadcrumbsItems" />

    <img
      v-if="organization?.dashboardHeaderImageUrl"
      :src="organization.dashboardHeaderImageUrl"
      alt="Header"
      class="w-full aspect-[3.4] rounded-lg mt-10 object-cover"
    />

    <EmptyList v-if="paginatedHub.nodes.length === 0" class="mt-30" />
    <div
      v-else-if="paginatedHub"
      class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-10"
    >
      <div
        v-for="(hub, hubIndex) in paginatedHub.nodes"
        :key="hubIndex"
        data-testid="hub-card"
        class="bg-surface rounded-lg aspect-[3/4] relative"
        :class="{ 'border-2 border-primary': hub.isLaunchEnabled }"
      >
        <launch-hub-badge
          v-if="hub.isLaunchEnabled"
          class="absolute left-2 top-2"
        />
        <div class="aspect-square w-full flex items-center justify-center">
          <NuxtLink :to="createHubUrl(hub.slug)" class="w-10/12 rounded-full">
            <img
              :src="hub.logoUrl"
              alt="Hub logo"
              class="aspect-square w-full rounded-full object-cover"
            />
          </NuxtLink>
        </div>

        <div class="px-5">
          <hr />

          <NuxtLink
            :to="createHubUrl(hub.slug)"
            class="truncate mt-4 block font-semibold text-on-surface"
          >
            {{ hub.title }}
          </NuxtLink>
          <div class="text-sm font-semibold text-on-surface-variant">
            {{ hub.contentsCount }} {{ $t('common.content-items') }}
          </div>
        </div>
      </div>

      <div
        v-if="
          organization &&
          organization.displayDashboardHelpBox &&
          organization.dashboardHelpBoxCtaButtonUrl
        "
        class="p-9 flex flex-col justify-center bg-secondary text-on-secondary rounded-lg"
      >
        <div>
          <div>{{ organization.dashboardHelpBoxTitle }}</div>
          <div class="font-bold text-3xl mt-3">
            {{ organization.dashboardHelpBoxDescription }}
          </div>
          <Button
            :to="organization.dashboardHelpBoxCtaButtonUrl"
            :label="organization.dashboardHelpBoxCtaButtonText ?? ''"
            :color="ButtonColor.SURFACE_CONTAINER"
            class="mt-8"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useOrganizationDomain } from '#imports';
import { useAsyncData } from 'nuxt/app';
import { apiGetHubsList } from '~/api/hubs';
import { useAuthUserStore } from '~/store/auth-user';
import { EmptyList } from '~/pages/_components/empty-list';
import { Button, ButtonColor, Breadcrumbs } from '@exfluential/ui';
import { useI18n } from 'vue-i18n';
import { handleApiError } from '~/api';
import { LaunchHubBadge } from '~/pages/_components/hub-dashboard/_components/launch-hub-badge';
import { useOrganizationStore } from '~/store/organization';
import { DashboardSearch } from '~/components/dashboard-search';

type Props = {
  /** Overrides the organization dashboard heading (used by the Hubs listing). */
  title?: string;
};

const props = defineProps<Props>();

const { t } = useI18n();
const authUser = useAuthUserStore();
const { organization } = storeToRefs(useOrganizationStore());
const organizationDomain = useOrganizationDomain();

const { data: paginatedHub, error: hubsError } = useAsyncData('hubs', () => {
  return apiGetHubsList({ perPage: 100 });
});

if (hubsError.value) {
  await handleApiError(hubsError.value);
}

const breadcrumbsItems = computed(() => {
  return organizationDomain !== null || !organization.value
    ? null
    : [
        { label: t('common.dashboard'), to: '/' },
        {
          label: organization.value.name,
          to: `/org/${organization.value.slug}`,
        },
      ];
});

const createHubUrl = (hubSlug: string) => {
  if (organizationDomain !== null || !organization.value) {
    return `/${hubSlug}`;
  }
  return `/org/${organization.value.slug}/${hubSlug}`;
};
</script>
